import { parse, parseSetCookie, serialize } from 'cookie-es'
import type { RouteLocationNormalizedLoaded } from 'vue-router'

export default () => {
    const loginRoute = '/auth/keycloak'
    const logoutRoute = '/auth/logout'
    const refreshRoute = '/auth/refresh'
    const impersonateRoute = '/auth/impersonate'
    const updateCredentialRoute = '/auth/update-credential'
    const getCredentialsRoute = '/auth/credentials'

    const setPreviousRoute = (route: RouteLocationNormalizedLoaded) => {
        const { fullPath } = route
        // We do not save the previous route if it is a keycloak or nuxt route
        if (
            !fullPath.includes('/auth/keycloak') &&
            !fullPath.includes('_nuxt')
        ) {
            useCookie('previousRoute', {
                secure: true,
                sameSite: 'lax',
                domain: useRuntimeConfig().public.domain || undefined
            }).value = useRequestURL().origin + fullPath
        }
    }

    const login = async () => {
        setPreviousRoute(useRoute())

        return navigateTo(loginRoute, {
            external: true
        })
    }

    const logout = async (usePreviousRoute: boolean = false) => {
        await useUserSession().clear()
        return navigateTo(
            `${logoutRoute}?usePreviousRoute=${usePreviousRoute}`,
            {
                external: true
            }
        )
    }

    const impersonate = async (userId: string) => {
        await $fetch(impersonateRoute, {
            params: {
                userId
            }
        })

        await useUserSession().fetch()
    }

    const refreshToken = async () => {
        const runtimeConfig = useRuntimeConfig()
        const nuxtApp = useNuxtApp()
        try {
            if (
                import.meta.client &&
                nuxtApp.isHydrating &&
                nuxtApp.payload.serverRendered
            ) {
                await $fetch(refreshRoute)
            } else {
                const serverEvent = useRequestEvent()

                await useRequestFetch()('/auth/refresh', {
                    onResponse({
                        response: { headers }
                    }: {
                        response: { headers: Headers }
                    }) {
                        // Forward the Set-Cookie header to the main server event
                        if (import.meta.server && serverEvent) {
                            for (const setCookie of headers.getSetCookie()) {
                                appendResponseHeader(
                                    serverEvent,
                                    'Set-Cookie',
                                    setCookie
                                )
                                // Update session cookie for next fetch requests
                                const { name, value } =
                                    parseSetCookie(setCookie)

                                if (name === runtimeConfig.session.name) {
                                    // console.log('updating headers.cookie to', value)
                                    const cookies = parse(
                                        serverEvent.headers.get('cookie') || ''
                                    )
                                    // set or overwrite existing cookie
                                    cookies[name] = value
                                    // update cookie event header for future requests
                                    serverEvent.headers.set(
                                        'cookie',
                                        Object.entries(cookies)
                                            .map(([name, value]) =>
                                                serialize(name, value)
                                            )
                                            .join('; ')
                                    )

                                    // Also apply to serverEvent.node.req.headers
                                    if (serverEvent.node?.req?.headers) {
                                        serverEvent.node.req.headers['cookie'] =
                                            serverEvent.headers.get('cookie') ||
                                            ''
                                    }
                                }
                            }
                        }
                    }
                })
            }
            await useUserSession().fetch()
        } catch {
            return navigateTo(loginRoute, {
                external: true
            })
        }
    }

    const updateCredential = async (keycloakAction: string, state: string) => {
        setPreviousRoute(useRoute())
        return navigateTo(
            `${updateCredentialRoute}?keycloakAction=${keycloakAction}&state=${state}`,
            {
                external: true
            }
        )
    }

    const getCredentials = () => $fetch(getCredentialsRoute)

    return {
        login,
        loginRoute,
        refreshRoute,
        impersonateRoute,
        logout,
        logoutRoute,
        updateCredential,
        getCredentials,
        impersonate,
        refreshToken,
        setPreviousRoute
    }
}
